import {
  type BinaryFilter,
  type BinaryOperator,
  type Filter,
  flattenFilters,
  type Query,
  type QueryOrder,
  type UnaryFilter,
} from "@cubejs-client/core";
import { z } from "zod/v4";
import {
  BOUND_OPERATORS,
  DATE_GRANULARITIES,
  type DateGranularity,
  DATE_OPERATORS,
  MAX_FILTER_DEPTH,
  MAX_GROUP_CHILDREN,
  MAX_LIMIT,
  TEXT_OPERATORS,
  VALUE_OPERATORS,
  VIDEO_LENGTH_OPERATORS,
} from "../constants";
import {
  type CubeMember,
  type CubeMeta,
  type CubeView,
  UNARY_OPERATORS,
} from "./cube-meta";

type MemberType = CubeMember["type"];
type Leaf = BinaryFilter | UnaryFilter;
type WireBinaryLeaf = Omit<BinaryFilter, "values"> & {
  values: (string | null)[];
};

const MAX_QUERY_LENGTH = 128 * 1024;
const MAX_FILTER_LEAVES = 500;
const MAX_VALUES = 1_000;
const MAX_VALUE_LENGTH = 2_048;

const QUERY_ORDERS = [
  "asc",
  "desc",
  "none",
] as const satisfies readonly QueryOrder[];
const RESPONSE_FORMATS = [
  "compact",
  "columnar",
  "default",
] as const satisfies readonly NonNullable<Query["responseFormat"]>[];

const OPERATOR_ALIASES: Record<string, string> = {
  in: "equals",
  notIn: "notEquals",
  onTheDate: "inDateRange",
};

const ABSOLUTE_DATE_TIME =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}(:?\d{2})?)?)?$/;
const CALENDAR_DAY = z.iso.date();

const enumOf = <T extends string>({
  names,
  message,
}: {
  names: readonly T[];
  message: string;
}): z.ZodType<T> => {
  const [first, ...rest] = names;
  return first === undefined
    ? z.never({ error: message })
    : z.enum([first, ...rest], { error: message });
};

const pairOf = (item: z.ZodType<string>) =>
  z.tuple([item, item]).transform(([from, to]): [string, string] => [from, to]);

export const dateRangeSchema = z.union([
  pairOf(z.string()),
  z.tuple([z.string()]).transform(([date]): [string, string] => [date, date]),
  z.string(),
]);

const wireValues = (value: z.ZodType<string, string>) =>
  z
    .array(
      z
        .union([z.string(), z.number(), z.boolean()])
        .transform(String)
        .pipe(
          value
            .refine((text) => text.length <= MAX_VALUE_LENGTH, {
              message: "value-too-long",
            })
            .refine((text) => !text.includes("\u0000"), {
              message: "value-contains-nul",
            }),
        )
        .nullable(),
    )
    .min(1, { message: "missing-value" })
    .max(MAX_VALUES, { message: "too-many-values" });

export const VALUE_SCHEMAS: Record<MemberType, z.ZodType<string, string>> = {
  string: z.string(),
  number: z
    .string()
    .refine((value) => value.trim() !== "" && Number.isFinite(Number(value)), {
      error: "not-a-number",
    }),
  time: z
    .string()
    .regex(ABSOLUTE_DATE_TIME, { error: "not-a-date" })
    .refine((value) => CALENDAR_DAY.safeParse(value.slice(0, 10)).success, {
      error: "not-a-date",
    }),
};

type OperatorGroup = {
  operators: readonly [BinaryOperator, ...BinaryOperator[]];
  values: (type: MemberType) => z.ZodType<string, string>;
};

const VALUE_GROUP: OperatorGroup = {
  operators: VALUE_OPERATORS,
  values: (type) => VALUE_SCHEMAS[type],
};
const TEXT_GROUP: OperatorGroup = {
  operators: TEXT_OPERATORS,
  values: () => z.string(),
};
const BOUND_GROUP: OperatorGroup = {
  operators: BOUND_OPERATORS,
  values: (type) => VALUE_SCHEMAS[type],
};
const VIDEO_LENGTH_GROUP: OperatorGroup = {
  operators: VIDEO_LENGTH_OPERATORS,
  values: () => VALUE_SCHEMAS.number,
};
const DATE_GROUP: OperatorGroup = {
  operators: DATE_OPERATORS,
  values: () => z.string(),
};

const groupsOf = ({ type, meta }: CubeMember): OperatorGroup[] => {
  if (meta.kind === "tag") {
    return [VALUE_GROUP];
  }
  if (meta.kind === "video-length") {
    return [VIDEO_LENGTH_GROUP];
  }
  switch (type) {
    case "time": {
      return [VALUE_GROUP, TEXT_GROUP, BOUND_GROUP, DATE_GROUP];
    }
    case "string":
    case "number": {
      return [VALUE_GROUP, TEXT_GROUP, BOUND_GROUP];
    }
    default: {
      return type satisfies never;
    }
  }
};

const binaryLeafSchema = (member: CubeMember) => {
  const [first, ...rest] = groupsOf(member).map((group) =>
    z.object({
      member: z.literal(member.name),
      operator: z.enum(group.operators),
      values: wireValues(group.values(member.type)),
    }),
  );
  if (first === undefined) {
    throw new Error(`Every member takes a binary filter: ${member.name}`);
  }
  return z.discriminatedUnion("operator", [first, ...rest], {
    error: "unsupported-operator",
  });
};

const unaryLeafSchema = (member: CubeMember) =>
  z.object({
    member: z.literal(member.name),
    operator: z.enum(UNARY_OPERATORS),
    values: z.unknown(),
  });

const presenceOf = (
  { member }: WireBinaryLeaf,
  operator: "set" | "notSet",
): UnaryFilter => ({ member, operator });

// Cube's BaseFilter drops null values and toggles IS NULL with them, as these arms spell out operator by operator.
function withoutNulls(
  leaf: WireBinaryLeaf,
  ctx: z.RefinementCtx<unknown>,
): Filter {
  const values = leaf.values.filter((value) => value !== null);
  const kept: BinaryFilter = { ...leaf, operator: leaf.operator, values };
  if (values.length === leaf.values.length) {
    return kept;
  }
  const reject = (message: string) => {
    ctx.addIssue({ code: "custom", message, input: leaf });
    return z.NEVER;
  };
  switch (leaf.operator) {
    case "equals":
    case "contains":
    case "startsWith":
    case "endsWith": {
      const nulls = presenceOf(leaf, "notSet");
      if (values.length > 0) {
        return { or: [kept, nulls] };
      }
      return leaf.operator === "equals" ? nulls : reject("missing-value");
    }
    case "notEquals":
    case "notContains":
    case "notStartsWith":
    case "notEndsWith": {
      const present = presenceOf(leaf, "set");
      if (values.length > 0) {
        return { and: [kept, present] };
      }
      return leaf.operator === "notEquals" ? present : reject("missing-value");
    }
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      return values.length > 0 ? kept : reject("missing-value");
    }
    case "inDateRange":
    case "notInDateRange":
    case "beforeDate":
    case "beforeOrOnDate":
    case "afterDate":
    case "afterOrOnDate": {
      return reject("null-date-value");
    }
    default: {
      return leaf.operator satisfies never;
    }
  }
}

// Cube's normalizeQueryFilters: a filter's dimension is its member, its values are strings, and in, notIn and onTheDate read as their aliases.
const normalizedLeaf = z
  .object({
    member: z.string().optional(),
    dimension: z.string().optional(),
    operator: z.string(),
    values: z.unknown(),
  })
  .transform(({ member, dimension, operator, values }) => ({
    member: member ?? dimension,
    operator: OPERATOR_ALIASES[operator] ?? operator,
    values,
  }));

const memberIssue = (issue: { input?: unknown }) =>
  typeof issue.input === "object" &&
  issue.input !== null &&
  "member" in issue.input &&
  issue.input.member !== undefined
    ? "unknown-member"
    : "filter-without-member";

const byMember = <Option extends z.core.$ZodTypeDiscriminable>(
  options: readonly Option[],
) => {
  const [first, ...rest] = options;
  return first === undefined
    ? z.never({ error: "unknown-member" })
    : z.discriminatedUnion("member", [first, ...rest], { error: memberIssue });
};

const measureFilterLeafSchema = (measure: CubeMember) =>
  z.object({
    member: z.literal(measure.name),
    operator: z.literal("measureFilter"),
    values: z.unknown(),
  });

// Cube's measureFilter adds a measure's model filters to the where; these measures declare none (conversions' events are the query's pick), so it adds nothing.
function leafSchema({
  dimensions,
  measures,
}: Pick<CubeView, "dimensions" | "measures">): z.ZodType<Filter> {
  const filterable = measures.filter(
    ({ meta }) => meta.kind !== "creative-media" && meta.kind !== "nested",
  );
  const members = [...dimensions, ...filterable];
  const presenceTestable = members.filter(
    ({ meta }) => meta.kind !== "video-length",
  );
  return normalizedLeaf
    .pipe(z.any())
    .pipe(
      z.union([
        byMember(members.map(binaryLeafSchema)).transform(withoutNulls),
        byMember(presenceTestable.map(unaryLeafSchema)).transform(
          ({ member, operator }): UnaryFilter => ({ member, operator }),
        ),
        byMember(filterable.map(measureFilterLeafSchema)).transform(
          (): Filter => ({ and: [] }),
        ),
      ]),
    );
}

type LeafMember = { kind: "member"; name: string } | { kind: "memberless" };

export const leafMemberOf = (leaf: Leaf): LeafMember => {
  const name = leaf.member ?? leaf.dimension;
  return name === undefined ? { kind: "memberless" } : { kind: "member", name };
};

export const orderOf = ({
  order,
}: Pick<Query, "order">): [string, QueryOrder][] =>
  Array.isArray(order) ? order : Object.entries(order ?? {});

// The bound runs before any element parses, so a huge group is rejected without walking it.
const boundedArray = <T>(element: z.ZodType<T>): z.ZodType<T[]> =>
  z
    .array(z.unknown())
    .max(MAX_GROUP_CHILDREN, { message: "too-many-children", abort: true })
    .pipe(z.array(element));

const isGroup = (value: unknown) =>
  typeof value === "object" &&
  value !== null &&
  ("and" in value || "or" in value);

const filterTree = ({
  leaf,
  depth,
}: {
  leaf: z.ZodType<Filter>;
  depth: number;
}): z.ZodType<Filter> => {
  if (depth === 0) {
    return z.union([
      leaf,
      z.custom<never>(() => false, {
        error: (issue) =>
          isGroup(issue.input) ? "filter-too-deep" : "not-a-filter",
      }),
    ]);
  }
  const child = filterTree({ leaf, depth: depth - 1 });
  return z.union([
    z.object({ and: boundedArray(child) }),
    z.object({ or: boundedArray(child) }),
    leaf,
  ]);
};

export const cubeFilterSchema = (cube: CubeView): z.ZodType<Filter> =>
  filterTree({
    leaf: leafSchema(cube),
    depth: MAX_FILTER_DEPTH,
  });

const timeDimensionSchemas = (cube: CubeView) =>
  cube.dimensions.flatMap((dimension) =>
    dimension.type === "time"
      ? [
          z
            .object({
              dimension: z.literal(dimension.name),
              granularity: z
                .enum(DATE_GRANULARITIES, { error: "unknown-granularity" })
                .optional(),
              dateRange: dateRangeSchema.optional(),
              compareDateRange: z
                .array(dateRangeSchema)
                .min(1, { error: "empty-compare-date-range" })
                .optional(),
            })
            .refine(
              ({ dateRange, compareDateRange }) =>
                dateRange === undefined || compareDateRange === undefined,
              { error: "date-range-and-compare-date-range" },
            ),
        ]
      : [],
  );

type Granular = { dimension: string; granularity: DateGranularity };

const granularDimensionsOf = (cube: CubeView): Map<string, Granular> =>
  new Map(
    cube.dimensions.flatMap((dimension) =>
      dimension.type === "time"
        ? DATE_GRANULARITIES.map((granularity): [string, Granular] => [
            `${dimension.name}.${granularity}`,
            { dimension: dimension.name, granularity },
          ])
        : [],
    ),
  );

// Cube's idOrMemberExpressionName: an order key need not name a member, and Cube drops one that names nothing selected.
const ORDER_KEY = /^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+){0,2}$/;

// Cube checks moment.tz.zone; the IANA names ICU knows are the same database, read without bundling it.
const timezoneSchema = z.string().transform((name, ctx) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: name,
    }).resolvedOptions().timeZone;
  } catch {
    ctx.addIssue({ code: "custom", message: "unknown-timezone", input: name });
    return z.NEVER;
  }
});

const rowCount = z
  .number()
  .int({ error: "limit-out-of-range" })
  .min(0, { error: "limit-out-of-range" })
  .max(MAX_LIMIT, { error: "limit-out-of-range" })
  .nullable()
  .optional();

const queryFieldsOf = (cube: CubeView) => {
  const granular = granularDimensionsOf(cube);
  const orderKey = z.string().regex(ORDER_KEY);
  const direction = z.enum(QUERY_ORDERS);
  const [firstTime, ...restTime] = timeDimensionSchemas(cube);
  const memberOf = (names: readonly string[]) =>
    z.array(enumOf({ names, message: "unknown-member" }));
  return {
    measures: memberOf(cube.measures.map(({ name }) => name)).optional(),
    dimensions: memberOf([
      ...cube.dimensions.flatMap(({ name, meta }) =>
        meta.kind === "video-length" ? [] : [name],
      ),
      ...granular.keys(),
    ]).optional(),
    segments: memberOf(cube.segments.map(({ name }) => name)).optional(),
    timeDimensions: z
      .array(
        firstTime === undefined
          ? z.never({ error: "unknown-member" })
          : z.discriminatedUnion("dimension", [firstTime, ...restTime], {
              error: "unknown-member",
            }),
      )
      .optional(),
    filters: boundedArray(cubeFilterSchema(cube))
      .refine(
        (filters) => flattenFilters(filters).length <= MAX_FILTER_LEAVES,
        { message: "too-many-filters" },
      )
      .optional(),
    order: z
      .union([
        z.array(
          z
            .tuple([orderKey, direction])
            .transform(([key, to]): [string, QueryOrder] => [key, to]),
        ),
        z.record(orderKey, direction),
      ])
      .optional(),
    limit: rowCount,
    rowLimit: rowCount,
    offset: z
      .number()
      .int({ error: "offset-out-of-range" })
      .min(0, { error: "offset-out-of-range" })
      .optional(),
    timezone: timezoneSchema.optional(),
    ungrouped: z.boolean().optional(),
    responseFormat: z.enum(RESPONSE_FORMATS).optional(),
    total: z.boolean().optional(),
    joinHints: z
      .array(z.array(z.literal(cube.name, { error: "no-join-path" })))
      .optional(),
  };
};

type ViewQuery = z.output<z.ZodObject<ReturnType<typeof queryFieldsOf>>>;

// Cube's normalizeQuery moves a granularity named in dimensions into timeDimensions.
const withGranularDimensions =
  (granular: ReadonlyMap<string, Granular>) =>
  (query: ViewQuery): ViewQuery => {
    const grained = (query.dimensions ?? []).flatMap((name) => {
      const named = granular.get(name);
      return named === undefined ? [] : [named];
    });
    return grained.length === 0
      ? query
      : {
          ...query,
          dimensions: (query.dimensions ?? []).filter(
            (name) => !granular.has(name),
          ),
          timeDimensions: [...(query.timeDimensions ?? []), ...grained],
        };
  };

const selectsSomething = ({
  measures,
  dimensions,
  timeDimensions,
}: ViewQuery) =>
  (measures ?? []).length > 0 ||
  (dimensions ?? []).length > 0 ||
  (timeDimensions ?? []).some(({ granularity }) => granularity !== undefined);

const ungroupedIssuesOf = ({
  cube,
  query,
}: {
  cube: CubeView;
  query: ViewQuery;
}): string[] => {
  if (query.ungrouped !== true) {
    return [];
  }
  const measures = new Set(cube.measures.map(({ name }) => name));
  const missingKeys = cube.dimensions.filter(
    ({ name, primaryKey }) =>
      primaryKey === true && !(query.dimensions ?? []).includes(name),
  );
  const measureFilters = flattenFilters(query.filters ?? []).filter((leaf) =>
    measures.has(leaf.member ?? ""),
  );
  return [
    ...(missingKeys.length > 0 ? ["ungrouped-without-primary-keys"] : []),
    ...(measureFilters.length > 0 ? ["ungrouped-measure-filter"] : []),
  ];
};

const queryIssuesOf = ({
  cube,
  query,
}: {
  cube: CubeView;
  query: ViewQuery;
}): string[] => [
  ...(selectsSomething(query) ? [] : ["nothing-to-select"]),
  ...((query.timeDimensions ?? []).filter(
    ({ compareDateRange }) => compareDateRange !== undefined,
  ).length > 1
    ? ["several-compare-date-ranges"]
    : []),
  ...ungroupedIssuesOf({ cube, query }),
];

function viewQuerySchema(cube: CubeView) {
  const granular = granularDimensionsOf(cube);
  return z
    .object(queryFieldsOf(cube))
    .transform(withGranularDimensions(granular))
    .superRefine((query, ctx) => {
      for (const message of queryIssuesOf({ cube, query })) {
        ctx.addIssue({ code: "custom", message, input: query });
      }
    });
}

export type CubeQuery = z.output<ReturnType<typeof viewQuerySchema>>;

// The size check reads the raw JSON before any view's schema walks it; z.any hands Cube's Query to the views, whose literals are narrower.
export const cubeQuerySchema = (meta: CubeMeta): z.ZodType<CubeQuery, Query> =>
  z
    .custom<Query>(
      (query) => JSON.stringify(query ?? null).length <= MAX_QUERY_LENGTH,
      { message: "query-too-large", abort: true },
    )
    .pipe(z.any())
    .pipe(z.union(meta.cubes.map(viewQuerySchema)));
